'use client';
import * as React from 'react';
import type { ComboboxStore } from '../store';

export interface ComboboxDerivedItemsContext {
  hasItems: boolean;
  filteredItems: any[];
  /**
   * `filteredItems` flattened across groups and projected to selection values. Identical to the
   * items themselves unless `items` is a `createItems()` collection.
   */
  flatFilteredValues: any[];
}

export const ComboboxRootContext = React.createContext<ComboboxStore | undefined>(undefined);
export const ComboboxDerivedItemsContext = React.createContext<
  ComboboxDerivedItemsContext | undefined
>(undefined);
export const ComboboxHasItemsContext = React.createContext<boolean>(false);
// `inputValue` can't be placed in the store.
// https://github.com/mui/base-ui/issues/2703
export const ComboboxInputValueContext =
  React.createContext<React.ComponentProps<'input'>['value']>('');

export function useComboboxRootContext() {
  const context = React.useContext(ComboboxRootContext) as ComboboxStore | undefined;
  if (!context) {
    throw new Error(
      'Base UI: ComboboxRootContext is missing. Combobox parts must be placed within <Combobox.Root>.',
    );
  }
  return context;
}

export function useComboboxDerivedItemsContext() {
  const context = React.useContext(ComboboxDerivedItemsContext);
  if (!context) {
    throw new Error(
      'Base UI: ComboboxItemsContext is missing. Combobox parts must be placed within <Combobox.Root>.',
    );
  }
  return context;
}

export function useComboboxInputValueContext() {
  return React.useContext(ComboboxInputValueContext);
}

export function useComboboxHasItemsContext() {
  return React.useContext(ComboboxHasItemsContext);
}
